export interface DesignState {
  isPublic: boolean;
  encPublicId?: string;
  designId?: number;
  encryptedDesignId?: string;
  structure?: DesignData;
  productTypeId?: number;
  productId?: number;
  orderItemId?: number;
  productUid?: string;
  internalUid?: string;
  quantity?: number;
  originalQuantity?: number;
  categoryId?: number;
  totalPagesCount?: number;
  hasStructureChanged: boolean;
  isLoaded: boolean;
  errors: DesignError[];
  abstractProduct?: AbstractProduct;
  selectedProductAttributes: { [typeName: string]: Attribute };

  isLoading: boolean;
  isSaving: boolean;

  isAgreeWithLowDPI: boolean;
  isLowDPIWarningEnabled: boolean;

  includeDpiEnhancement: boolean;
  includeCanvasHangerKit: boolean;
}

export interface AbstractProduct {
  abstractProductId: number;
  categoryId: number;
  productTypeId: number;
  baseProductUid: string;
  productPageSelectors: Selector[];
  products: Product[];
  filteredProducts: Product[];
  editorSelectors: Selector[];
  name: string;
  slug: string;
}

export interface Selector {
  type?: 'complex' | 'color' | 'product-size';
  typeName: string;
  name: string;
  description?: string;
  detailsTitle?: string;
  details?: string;
  isHidden?: boolean;
  options: SelectorOption[];
  skipPriceShowing?: boolean;
}

export interface Attribute {
  typeName: string;
  value: string;
}

export interface Product {
  productId: number;
  productTypeId: number;
  paperFormatId: number;
  paperTypeId: number;
  colorTypeId: number;
  productUid: string;
  internalUid: string;
  abstractProductId: number;
  previews: string[];
  attributes: Attribute[];
}

export interface SelectorOption {
  value: string;
  name: string;
  hexColor?: string;
  hexColor2?: string;
  order: number;
  iconUrl?: string | null;
  description?: string | null;
}

export enum DesignError {
  uploadInProgress = 'uploadInProgress',
}

// COPIED FROM EDITOR
// https://github.com/gelatoas/gelato-api-ui-design-editor/blob/staging/src/store/design/types.ts
// Should be extracted to separate module and included via node_modules

export interface PageCountLimit {
  min: number;
  max: number;
  increment: number;
}

export interface ProductVariant {
  product_uid: string;
  types: string[];
}

export interface DesignData {
  product_uid: string;
  spreads: Spread[];
  page_count?: number;
  page_count_limit?: PageCountLimit;
  dimensions?: DesignDimensions;
  warnings?: Warning[];
  resolution?: Resolution;
  variants?: ProductVariant[];
}

type Resolution = {
  dpi: {
    max: number;
    min: number;
    ok: number;
  };
};

type Warning = DPIWarning | NotVisibleWarning | OutOfRangeWarning | MaxStitchCountWarning | MaxColorCountWarning;

interface SimpleWarning {
  elementAddress: {
    spreadIndex: number;
    pageIndex: number;
    elementIndex: number;
  };
}

interface SpreadWarning {
  spreadIndex: number;
  count: number;
}

interface DPIWarning extends SimpleWarning {
  type: 'LowDPI';
  dpi: number;
}

interface NotVisibleWarning extends SimpleWarning {
  type: 'NotVisible';
}

interface OutOfRangeWarning extends SimpleWarning {
  type: 'OutOfRange';
}

export interface MaxStitchCountWarning extends SpreadWarning {
  type: 'MaxStitchCount';
}

export interface MaxColorCountWarning extends SpreadWarning {
  type: 'MaxColorCount';
}
interface DesignDimensions {
  max_width: number;
  height: number;
  width: number;
  min_width: number;
  max_height: number;
  min_height: number;
}
export interface Spread {
  pages: Page[];
  name: string;
  layoutSchemaName?: string;
}

export interface Page {
  x: number;
  y: number;
  width: number;
  height: number;
  page_nr: number;
  groups: Groups;
  layoutSchemaName?: string;
}

export interface Groups {
  bleed?: Bleed[];
  mediabox?: MediaBox[];
  content?: Content[];
  media?: MediaElement[];
  diecut?: Diecut[];
  blank?: MediaBox[];
}

export interface GroupElement {
  type: string;
  name: string;
  group: string;
  x: number;
  y: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  width: number;
  height: number;
  path?: string;
  extra?: {
    [key: string]: any;
  };
}

export interface Bleed extends GroupElement {
  path: string;
}

export interface Diecut extends GroupElement {
  path: string;
  sx: number;
  sy: number;
}

export interface MediaBox extends GroupElement {

}

export interface Content extends GroupElement {
  margin?: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  } | number;
  title?: string;
  description?: string[];
}

export interface MediaElement extends GroupElement {
  name: string;
  locked: boolean;
  personalizationLocked: boolean;
  zIndex: number;
  uuid: number;
  r: number;
  sample?: boolean;
  url?: string;
  imageId?: string;
}

export interface MediaImage extends MediaElement {
  url: string;
  px: number;
  py: number;
  pw: number;
  ph: number;
  pr: number;
  extra?: {
    piw?: number;
    pih?: number;
  };
}

export interface MediaText extends MediaElement {
  px: number;
  py: number;
  pw: number;
  ph: number;
  pr: number;
  sx: number;
  sy: number;
  fill: string;
  path: string;
  extra: {
    text: string;
    fontFamily: string;
    textAlign: string;
    fontSize: number;
    lineHeight: number;
    italic: boolean;
    fontWeight: string;
    underline: boolean;
    textDirection: string;
  };
}

export interface MediaAddon extends MediaElement {
  addonId: string;
  subType: string;
  px: number;
  py: number;
  pw: number;
  ph: number;
  pr: number;
}

// EOF COPIED FROM EDITOR
